<template>
  <div class="record-library-wrap">
    <div class="record-library-toolbar">
      <span class="filter-label">类型</span>
      <el-select
        v-model="typeFilter"
        clearable
        size="mini"
        placeholder="全部"
        style="width: 120px"
      >
        <el-option
          v-for="opt in typeOptions"
          :key="opt"
          :label="opt"
          :value="opt"
        />
      </el-select>
      <span v-if="rows.length" class="hint">
        共 {{ displayRows.length }}{{ displayRows.length !== rows.length ? ` / ${rows.length}` : '' }} 条
      </span>
    </div>
    <div class="table-scroll">
      <el-table
        v-loading="loading"
        :data="displayRows"
        border
        size="mini"
        :max-height="maxHeight"
        :height="height"
        class="record-library-table"
        :empty-text="emptyText"
      >
        <el-table-column v-if="showLibraryCheck" label="记录库" width="72" align="center" fixed>
          <template slot-scope="{ row }">
            <el-checkbox
              :value="isInLibrary(row)"
              :disabled="!!row._libBusy"
              @change="(val) => $emit('toggle-library', row, val)"
            />
          </template>
        </el-table-column>
        <el-table-column prop="name" label="股票名称" min-width="88" fixed show-overflow-tooltip sortable />
        <el-table-column prop="firstBoardDate" label="首板时间" min-width="100" sortable />
        <el-table-column label="排名" min-width="200">
          <template slot-scope="{ row }">
            <span class="rank-tags">
              <el-tag v-if="row.rankT" size="mini" type="danger">T{{ row.rankT }}</el-tag>
              <el-tag v-if="row.rankB" size="mini" type="warning">B{{ row.rankB }}</el-tag>
              <el-tag v-if="row.rankJ2" size="mini" type="success">2J{{ row.rankJ2 }}</el-tag>
              <el-tag v-if="row.rankJ3" size="mini" type="info">3J{{ row.rankJ3 }}</el-tag>
              <span v-if="!row.rankT && !row.rankB && !row.rankJ2 && !row.rankJ3">-</span>
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="chipV2Score" label="筹码V2" min-width="100" sortable>
          <template slot-scope="{ row }">
            <span v-if="row.chipV2Score != null">{{ fmtChipV2Score(row) }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="rangeAllD0" label="全周期粘合" min-width="120" sortable :formatter="fmtRangeAllD0" />
        <el-table-column prop="priceToMax" label="首板价格位置" min-width="170" sortable>
          <template slot-scope="{ row }">
            <span :class="{ 'chip-v2-overextended': row.pricePositionLevel === 'OVEREXTENDED' }">
              {{ fmtPriceToMaxWithLabel(row) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          prop="freeFloatMarketCap"
          label="自由流通市值(亿)"
          min-width="150"
          sortable
          :formatter="fmtNum"
        />
        <el-table-column prop="ma250" label="MA250" min-width="100" sortable :formatter="fmtNum" />
        <el-table-column
          prop="distToYearHighPct"
          label="距年高涨幅"
          min-width="140"
          sortable
          :formatter="fmtDistYearHigh"
        />
        <el-table-column label="类型" min-width="110" prop="type" sortable>
          <template slot-scope="{ row }">
            <el-select
              v-if="editable && isEditing(row)"
              :value="row._editType"
              size="mini"
              clearable
              placeholder="选择"
              style="width: 100%"
              popper-append-to-body
              @click.native.stop
              @change="(val) => onEditTypeChange(row, val)"
            >
              <el-option
                v-for="opt in typeOptions"
                :key="opt"
                :label="opt"
                :value="opt"
              />
            </el-select>
            <span v-else>{{ row.type || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="最终连板高度" min-width="120" prop="finalBoards" sortable>
          <template slot-scope="{ row }">
            <el-input
              v-if="editable && isEditing(row)"
              v-model="row._editFinalBoards"
              size="mini"
              @click.native.stop
            />
            <span v-else>{{ row.finalBoards != null ? row.finalBoards : '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="爆量" min-width="80" :formatter="fmtBaoVol" />
        <el-table-column
          prop="touchBoardNext"
          label="爆量日摸板"
          min-width="130"
          sortable
          :formatter="fmtBool"
        />
        <el-table-column prop="totalScore" label="综合分" min-width="90" sortable>
          <template slot-scope="{ row }">
            <span v-if="row.totalScore != null">{{ row.totalScore }}</span>
            <span v-else>-</span>
            <div v-if="row.starTier || hasNodeCore(row)" class="score-marks">
              <div v-if="row.starTier" :class="starClass(row.starTier)">{{ starText(row.starTier) }}</div>
              <span
                v-if="hasNodeCore(row)"
                class="node-mark"
                title="首板日节点达标（贪心V2 ≥7）"
              >⬡</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="nodeScore" label="节点分" min-width="80" sortable>
          <template slot-scope="{ row }">
            <span
              v-if="row.nodeScore != null"
              :class="{ 'node-score-hit': row.nodeHit || row.nodeCore }"
              :title="nodeScoreTitle(row)"
            >{{ row.nodeScore }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="chipScore" label="筹码分" min-width="90" sortable>
          <template slot-scope="{ row }">
            {{ row.chipScore != null ? row.chipScore : '-' }}
            <span v-if="row.cLevel" class="sub-tag">{{ row.cLevel }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="auctionScore" label="竞价分" min-width="100" sortable>
          <template slot-scope="{ row }">
            {{ row.auctionScore != null ? row.auctionScore : '-' }}
            <span v-if="row.baoAuctionLeg || row.jLevel" class="sub-tag">
              {{ [row.baoAuctionLeg, row.jLevel].filter(Boolean).join(' ') }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="shortChip" label="短期筹码分" min-width="130" sortable :formatter="fmtChipPct" />
        <el-table-column prop="midChip" label="中期筹码分" min-width="130" sortable :formatter="fmtChipPct" />
        <el-table-column prop="longChip" label="长期筹码分" min-width="130" sortable :formatter="fmtChipPct" />
        <el-table-column prop="chipScoreSum" label="筹码总分" min-width="120" sortable :formatter="fmtChipSumCol" />
        <el-table-column prop="gapChipSum" label="补涨筹码分" min-width="130" sortable :formatter="fmtGapChipCol" />
        <el-table-column prop="fullChip" label="全周期筹码分" min-width="145" sortable :formatter="fmtChipPct" />
        <el-table-column prop="chipSlope" label="筹码分斜率" min-width="130" sortable :formatter="fmtSlope" />
        <el-table-column prop="mainNetInflow0" label="首板净流入(亿)" min-width="150" sortable :formatter="fmtYi" />
        <el-table-column prop="mainNetInflow1" label="次日净流入(亿)" min-width="150" sortable :formatter="fmtYi" />
        <el-table-column prop="mainNetInflow2" label="次次日净流入(亿)" min-width="160" sortable :formatter="fmtYi" />
        <el-table-column
          prop="netInflowToAmount0"
          label="首板净流入/成交额"
          min-width="175"
          sortable
          :formatter="fmtRatioPct"
        />
        <el-table-column
          prop="netInflowToAmount1"
          label="次日净流入/成交额"
          min-width="175"
          sortable
          :formatter="fmtRatioPct"
        />
        <el-table-column
          prop="netInflowToAmount2"
          label="次次日净流入/成交额"
          min-width="185"
          sortable
          :formatter="fmtRatioPct"
        />
        <el-table-column prop="auctionGain2b" label="2B竞价涨幅" min-width="130" sortable :formatter="fmtAuctionNum" />
        <el-table-column
          prop="auctionTurnover2b"
          label="2B竞价实际换手"
          min-width="155"
          sortable
          :formatter="fmtAuctionNum"
        />
        <el-table-column
          prop="auctionVolumeRatio2b"
          label="2B竞价量比"
          min-width="130"
          sortable
          :formatter="fmtAuctionNum"
        />
        <el-table-column
          prop="auctionKillScore2b"
          label="2B竞价绝杀分"
          min-width="145"
          sortable
          :formatter="fmtAuctionNum"
        />
        <el-table-column prop="auctionGain3b" label="3B竞价涨幅" min-width="130" sortable :formatter="fmtAuctionNum" />
        <el-table-column
          prop="auctionTurnover3b"
          label="3B竞价实际换手"
          min-width="155"
          sortable
          :formatter="fmtAuctionNum"
        />
        <el-table-column
          prop="auctionVolumeRatio3b"
          label="3B竞价量比"
          min-width="130"
          sortable
          :formatter="fmtAuctionNum"
        />
        <el-table-column
          prop="auctionKillScore3b"
          label="3B竞价绝杀分"
          min-width="145"
          sortable
          :formatter="fmtAuctionNum"
        />
        <el-table-column v-if="editable" label="操作" width="120" fixed="right">
          <template slot-scope="{ row }">
            <el-button v-if="!isEditing(row)" type="text" size="mini" @click="$emit('start-edit', row)">
              编辑
            </el-button>
            <el-button
              v-else
              type="text"
              size="mini"
              style="color:#409eff"
              :loading="row._saving"
              @click="$emit('save-edit', row)"
            >
              保存
            </el-button>
            <el-button type="text" size="mini" style="color:#f56c6c" @click="$emit('remove', row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { formatSlopeX1000Pct } from '@/utils/hfbChipFilters'

export const RECORD_LIBRARY_TYPES = ['龙头', '补涨']

function chipSumOf(row) {
  const s = Number(row.shortChip)
  const m = Number(row.midChip)
  const l = Number(row.longChip)
  if (!Number.isFinite(s) || !Number.isFinite(m) || !Number.isFinite(l)) return null
  return s + m + l
}

function gapChipOf(row) {
  let v = Number(row.gapChipSum)
  if (Number.isFinite(v)) return v
  const m = Number(row.midChip)
  const l = Number(row.longChip)
  if (!Number.isFinite(m) || !Number.isFinite(l)) return null
  return m + l
}

export default {
  name: 'RecordLibraryTable',
  props: {
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    editable: { type: Boolean, default: false },
    editingId: { type: String, default: '' },
    showLibraryCheck: { type: Boolean, default: false },
    libraryKeySet: { type: Object, default: () => ({}) },
    maxHeight: { type: [Number, String], default: undefined },
    height: { type: [Number, String], default: undefined },
    emptyText: { type: String, default: '暂无记录' },
    /** @deprecated 已无版本切换 */
    initialScoreVersion: { type: String, default: 'FINAL' }
  },
  data() {
    return {
      typeFilter: '',
      typeOptions: RECORD_LIBRARY_TYPES
    }
  },
  watch: {},
  computed: {
    displayRows() {
      // 保持与 props.rows 同一对象引用，便于编辑态
      const base = (this.rows || []).map((r) => {
        if (r.chipScoreSum == null) {
          const sum = chipSumOf(r)
          if (sum != null) this.$set(r, 'chipScoreSum', sum)
        }
        if (r.gapChipSum == null) {
          const g = gapChipOf(r)
          if (g != null) this.$set(r, 'gapChipSum', g)
        }
        return r
      })
      if (!this.typeFilter) return base
      return base.filter((r) => r.type === this.typeFilter)
    }
  },
  methods: {
    onScoreVersionChange() {},
    rowKey(row) {
      return `${row.code || ''}|${row.firstBoardDate || ''}`
    },
    isInLibrary(row) {
      const id = this.libraryKeySet && this.libraryKeySet[this.rowKey(row)]
      return !!id
    },
    isEditing(row) {
      return !!row?.id && this.editingId === row.id
    },
    onEditTypeChange(row, val) {
      this.$set(row, '_editType', val == null ? '' : String(val))
    },
    fmtNum(_r, _c, v) {
      if (v == null || v === '') return '-'
      const n = Number(v)
      if (!Number.isFinite(n)) return '-'
      return Number(n.toFixed(2))
    },
    fmtBaoVol(row) {
      const isBao2 = row && row.bao2 === true
      const isBao3 = row && row.bao3 === true
      if (isBao2 && isBao3) return '爆2|爆3'
      if (isBao2) return '爆2'
      if (isBao3) return '爆3'
      if (row && (row.bao2 === false || row.bao3 === false)) return '缩量'
      return '-'
    },
    fmtBool(_r, _c, v) {
      if (v === true) return '是'
      if (v === false) return '否'
      return '-'
    },
    fmtChipPct(_r, _c, v) {
      if (v == null || v === '') return '-'
      const n = Number(v)
      if (!Number.isFinite(n)) return '-'
      return `${(n * 100).toFixed(2)}%`
    },
    fmtChipV2Score(row) {
      const s = Number(row?.chipV2Score)
      if (!Number.isFinite(s)) return '-'
      const tag = s >= 90 ? '强' : s >= 70 ? '中' : '弱'
      return `${s}｜${tag}`
    },
    fmtRangeAllD0(_r, _c, v) {
      if (v == null || v === '') return '-'
      const n = Number(v)
      if (!Number.isFinite(n)) return '-'
      return `${(n * 100).toFixed(1)}%`
    },
    fmtPriceToMaxWithLabel(row) {
      const v = row?.priceToMax
      if (v == null || v === '') return '-'
      const n = Number(v)
      if (!Number.isFinite(n)) return '-'
      const sign = n > 0 ? '+' : ''
      const pct = `${sign}${(n * 100).toFixed(1)}%`
      const label = row?.pricePositionLabel ? ` ${row.pricePositionLabel}` : ''
      return `${pct}${label}`
    },
    fmtChipSumCol(row, _c, v) {
      const n = v != null ? Number(v) : chipSumOf(row)
      return this.fmtChipPct(null, null, n)
    },
    fmtGapChipCol(row, _c, v) {
      const n = v != null ? Number(v) : gapChipOf(row)
      return this.fmtChipPct(null, null, n)
    },
    fmtSlope(_r, _c, v) {
      return formatSlopeX1000Pct(v)
    },
    fmtDistYearHigh(_r, _c, v) {
      if (v == null || v === '') return '-'
      const n = Number(v)
      if (!Number.isFinite(n)) return '-'
      return `${n.toFixed(2)}%`
    },
    fmtAuctionNum(_r, _c, v) {
      if (v == null || v === '') return '-'
      const n = Number(v)
      if (!Number.isFinite(n)) return '-'
      return Number(n.toFixed(2))
    },
    fmtYi(_r, _c, v) {
      if (v == null || v === '') return '-'
      const n = Number(v)
      if (!Number.isFinite(n)) return '-'
      return Number((n / 1e8).toFixed(2))
    },
    fmtRatioPct(_r, _c, v) {
      if (v == null || v === '') return '-'
      const n = Number(v)
      if (!Number.isFinite(n)) return '-'
      return `${(n * 100).toFixed(2)}%`
    },
    starText(tier) {
      if (tier === 'pink5') return '★★★★★'
      if (tier === 'blue3') return '★★★'
      return ''
    },
    starClass(tier) {
      if (tier === 'pink5') return 'stars-pink'
      if (tier === 'blue3') return 'stars-blue'
      return ''
    },
    hasNodeCore(row) {
      return !!(row && (row.nodeCore || row.nodeHit))
    },
    nodeScoreTitle(row) {
      const hits = Array.isArray(row.nodeHits) ? row.nodeHits.join('、') : ''
      const fb = row.firstBoardDate || ''
      const parts = []
      if (fb) parts.push(`首板日 ${fb}`)
      if (hits) parts.push(`命中 ${hits}`)
      if (row.nodeHit || row.nodeCore) parts.push('达标≥7')
      return parts.join(' · ') || '节点分'
    }
  }
}
</script>

<style scoped>
.record-library-wrap {
  min-width: 0;
  width: 100%;
}
.record-library-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.filter-label {
  color: #606266;
  font-size: 13px;
}
.hint {
  color: #909399;
  font-size: 12px;
}
.table-scroll {
  min-width: 0;
  width: 100%;
  overflow: hidden;
}
.rank-tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}
.record-library-table {
  width: 100%;
}
.record-library-table ::v-deep .el-table__header th .cell {
  white-space: nowrap;
}
.record-library-table ::v-deep .el-table__body-wrapper {
  overflow-x: auto !important;
}
.sub-tag {
  margin-left: 4px;
  color: #909399;
  font-size: 11px;
}
.stars-pink {
  color: #ff69b4;
  font-weight: 700;
  letter-spacing: 1px;
  font-size: 11px;
  line-height: 1.2;
}
.stars-blue {
  color: #409eff;
  font-weight: 700;
  letter-spacing: 1px;
  font-size: 11px;
  line-height: 1.2;
}
.chip-v2-overextended {
  color: #e6a23c;
  font-weight: 600;
}
.score-marks {
  line-height: 1.2;
}
.node-mark {
  display: inline-block;
  margin-top: 1px;
  color: #13c2c2;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}
.node-score-hit {
  color: #13c2c2;
  font-weight: 700;
}
.vol-boost-mark {
  display: none;
}
</style>
